import { BeneficiaryFilters, FilterTemplate } from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { useAsyncData } from '@repo/ui/dataProvider'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import { useT } from '@repo/ui/hooks'
import { InlineCode } from '@repo/ui/inlineCode'
import { Input } from '@repo/ui/input'
import { Label } from '@repo/ui/label'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { onInput } from '@repo/ui/utils'
import { PenLineIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'sonner'

import { ReadonlyFilterList } from '@/components/beneficiary/list/filters/ReadonlyFilterList'
import { ResetAndUpdate } from '@/components/ResetAndUpdate'
import { selectSearchText } from '@/store/beneficiary-list-filters/selectors'
import { updateFilterTemplate } from '@/store/beneficiary-list-filters/thunks'
import { getCurrentOrganization } from '@/store/helpers'
import { useStoreDispatch, useStoreState } from '@/store/hooks'

const keyPrefix = 'beneficiary.filters.creator'
export function FilterTemplateEditor({
  disabled,
  filterTemplate,
  isNameUnique,
}: {
  disabled: boolean
  filterTemplate: FilterTemplate
  isNameUnique: (name: string, id: string) => boolean
}) {
  const dispatch = useStoreDispatch()
  const { t, tc } = useT({ keyPrefix })

  const [open, setOpen] = useState(false)

  const handleUpdate = (name: string) => {
    if (!filterTemplate) return
    if (name.trim().length === 0) return toast.error(t('nameRequired'))

    void dispatch(
      updateFilterTemplate({
        name,
        filters: filterTemplate.filters,
      }),
    )
  }
  if (!filterTemplate) return null
  return (
    <>
      <TooltipWrapper content={tc('edit', { cfl: true })}>
        <Button size="icon" variant="ghost" onClick={() => setOpen(true)}>
          <PenLineIcon className="size-4" />
        </Button>
      </TooltipWrapper>

      <Form
        disabled={disabled}
        onUpdate={handleUpdate}
        modalHandler={{ open, setOpen }}
        filters={filterTemplate.filters}
        initialName={filterTemplate.name}
        isNameUnique={(name) => isNameUnique(name, filterTemplate.id)}
      />
    </>
  )
}

function Form({
  filters,
  disabled,
  initialName,
  onUpdate,
  modalHandler,
  isNameUnique,
}: {
  filters: BeneficiaryFilters
  disabled: boolean
  isNameUnique: (name: string) => boolean
  initialName: string
  onUpdate: (name: string) => void
  modalHandler: { open: boolean; setOpen: (open: boolean) => void }
}) {
  const { t } = useT({ keyPrefix })
  const {
    data: [organization],
  } = useAsyncData([getCurrentOrganization])

  const schema = organization?.structuredDataSchema || []

  const [value, setValue] = useState(initialName)
  const { tc } = useT()
  useEffect(() => {
    setValue(initialName)
  }, [initialName])

  const handleReset = () => {
    setValue(initialName)
  }

  const handleUpdate = () => {
    if (!isNameUnique(value)) {
      return toast.error(t('nameMustBeUnique'))
    }
    onUpdate(value)
    modalHandler.setOpen(false)
  }

  const searchText = useSelector(selectSearchText)

  const beneficiaryCount = useStoreState((state) => state.beneficiaryList.pagination.totalItems)
  return (
    <Dialog
      open={modalHandler.open}
      defaultOpen={modalHandler.open}
      onOpenChange={modalHandler.setOpen}
      modal
    >
      <DialogContent className="max-h-[75vh]">
        <DialogHeader>
          <DialogTitle>{tc('edit', { cfl: true })}</DialogTitle>
          <DialogDescription>
            {tc('filterTemplate', { cfl: true, colon: true })} {initialName}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label>{tc('name', { cfl: true, colon: true })}</Label>
            <Input
              value={value}
              placeholder={tc('name', { cfl: true })}
              onChange={onInput(setValue)}
            />
          </div>

          <div className="flex flex-col gap-4 border-t-4 border-primary-50 pt-4">
            <Label className="text-base">{tc('filters', { cfl: true })}</Label>

            {searchText && (
              <div className="flex flex-col gap-1">
                <Label>{tc('searchTerm', { cfl: true, colon: true })}</Label>
                <InlineCode className="select-none">{searchText}</InlineCode>
              </div>
            )}

            <ReadonlyFilterList filters={filters} schema={schema} />
          </div>

          <div className="flex flex-col gap-4 border-t-4 border-primary-50 pt-4">
            <Label className="text-base">{tc('result', { cfl: true })}</Label>

            <div className="flex flex-col gap-1">
              <InlineCode className="select-none">
                {tc('beneficiary', { count: beneficiaryCount, cfl: true })}
              </InlineCode>
            </div>
          </div>
        </div>

        <DialogFooter>
          <ResetAndUpdate
            onReset={handleReset}
            onUpdate={handleUpdate}
            disabled={disabled && value === initialName}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
